// services/ApiService.js - COMPLETE & PRODUCTION READY
import { API_CONFIG, apiClient, IS_DEVELOPMENT } from '../config/apiConfig';

// ---------- MOCK DATA FOR INNOVATION MODULE ----------
const MOCK_INNOVATIONS = [
  {
    id: '1',
    title: 'OpenAI GPT-4o Candidate Matcher',
    description: 'Advanced language model that understands candidate nuances and job requirements with 96% accuracy.',
    type: 'ai_ml',
    stage: 'pilot',
    impact: 'critical',
    estimated_roi: 500000,
    budget: 150000,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
    owner: { name: 'Sarah Chen', id: 'user1' },
    team_id: 'team1',
    tags: ['OpenAI', 'GPT-4o', 'Matching', 'LLM'],
    progress: 75,
    ml_generated: true,
    ai_mode: 'OPENAI',
    created_at: new Date().toISOString(),
    timeline: '3 months',
    confidence: 96,
  },
  {
    id: '2',
    title: 'DeepSeek R1 Reasoning Engine',
    description: 'Chain-of-thought reasoning system that analyzes candidate responses with explainable AI.',
    type: 'ai_ml',
    stage: 'pilot',
    impact: 'critical',
    estimated_roi: 450000,
    budget: 135000,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
    owner: { name: 'Sarah Chen', id: 'user1' },
    team_id: 'team1',
    tags: ['DeepSeek', 'R1', 'Reasoning', 'Explainable AI'],
    progress: 75,
    ml_generated: true,
    ai_mode: 'DEEPSEEK',
    created_at: new Date().toISOString(),
    timeline: '4 months',
    confidence: 94,
  },
  {
    id: '3',
    title: 'Predictive Hiring Analytics',
    description: 'Forecast hiring needs using predictive models and historical data.',
    type: 'analytics',
    stage: 'build',
    impact: 'high',
    estimated_roi: 320000,
    budget: 95000,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    owner: { name: 'Alex Rivera', id: 'user3' },
    team_id: 'team3',
    tags: ['Analytics', 'Forecasting', 'Predictive'],
    progress: 45,
    ml_generated: false,
    created_at: new Date().toISOString(),
    timeline: '5 months',
    confidence: 85,
  },
];

const MOCK_INNOVATION_METRICS = [{
  active_experiments: 12,
  automation_coverage: 45,
  ai_accuracy: 92,
  time_saved: 340,
  roi: 156,
  pipeline_value: 1250000,
}];

const MOCK_TEAMS = [
  { id: 'team1', name: 'OpenAI & DeepSeek Lab', members: 8 },
  { id: 'team2', name: 'Analytics Team', members: 5 },
];

const MOCK_USERS = [
  { id: 'user1', name: 'Sarah Chen', role: 'AI Lead' },
  { id: 'user2', name: 'Mike Ross', role: 'Product Manager' },
  { id: 'user3', name: 'Alex Rivera', role: 'Data Scientist' },
  { id: 'user4', name: 'Priya Patel', role: 'Product Manager' },
];

const MOCK_ROADMAPS = [
  { id: 'r1', quarter: 'Q1 2024', title: 'OpenAI GPT-4o Integration', status: 'completed', progress: 100 },
  { id: 'r2', quarter: 'Q2 2024', title: 'DeepSeek R1 Reasoning', status: 'in_progress', progress: 65 },
  { id: 'r3', quarter: 'Q3 2024', title: 'Predictive Analytics', status: 'planned', progress: 0 },
];

const MOCK_BUSINESS_PLANS = [
  {
    id: 'bp1',
    title: 'AI-Powered Recruitment 2024 Strategic Plan',
    company_name: 'JobEng AI Solutions',
    status: 'draft',
    created_at: new Date().toISOString(),
  },
];

class ApiService {
  constructor() {
    this.client = apiClient;
    this.useMockData = IS_DEVELOPMENT;
    this.debug = API_CONFIG.DEBUG;
  }

  // ---------- INITIALIZATION ----------
  async initialize() {
    try {
      const response = await this.client.get(API_CONFIG.UTILS.HEALTH);
      const connected = response.ok && response.status === 200;
      console.log('✅ ApiService initialized:', connected ? 'Connected' : 'Offline');
      return { success: true, connected };
    } catch (error) {
      console.warn('⚠️ ApiService initialization warning:', error.message);
      return { success: true, connected: false };
    }
  }

  // ---------- RAW REQUEST WRAPPER ----------
  async _request(method, endpoint, data = null, params = {}) {
    try {
      let response;
      switch (method) {
        case 'GET':
          response = await this.client.get(endpoint, params);
          break;
        case 'POST':
          response = await this.client.post(endpoint, data);
          break;
        case 'PUT':
          response = await this.client.put(endpoint, data);
          break;
        case 'DELETE':
          response = await this.client.delete(endpoint);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      return {
        success: response.ok,
        status: response.status,
        data: response.data,
        is404: response.status === 404,
        isMock: false,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Request failed',
        is404: false,
        isMock: false,
      };
    }
  }

  // ---------- UNIVERSAL HANDLER WITH MOCK FALLBACK ----------
  async _handleRequest(method, endpoint, options = {}, mockData, name = '', isMutation = false) {
    const { data = null, params = {} } = options;
    const result = await this._request(method, endpoint, data, params);

    if (result.success) {
      if (this.debug) console.log(`✅ ${name} success`);
      return result;
    }

    // ✅ CRITICAL: Always fall back to mock data in development
    if (this.useMockData) {
      console.warn(`⚠️ ${name} failed (${result.status || 'network'}), using mock data`);
      const mock = typeof mockData === 'function' ? mockData() : mockData;
      return {
        success: true,
        status: 200,
        data: isMutation ? mock : (Array.isArray(mock) ? mock : mock),
        isMock: true,
      };
    }

    return result;
  }

  // ---------- INNOVATION CRUD ----------
  async getInnovations(params = {}) {
    return this._handleRequest(
      'GET',
      API_CONFIG.MODULES.INNOVATION.PROJECTS,
      { params },
      MOCK_INNOVATIONS,
      'getInnovations'
    );
  }

  async createInnovation(data) {
    return this._handleRequest(
      'POST',
      API_CONFIG.MODULES.INNOVATION.PROJECTS,
      { data },
      () => ({ id: Date.now().toString(), ...data, created_at: new Date().toISOString(), progress: 0 }),
      'createInnovation',
      true
    );
  }

  async updateInnovation(id, data) {
    const endpoint = `${API_CONFIG.MODULES.INNOVATION.PROJECTS}/${id}`;
    return this._handleRequest(
      'PUT',
      endpoint,
      { data },
      () => ({ id, ...data }),
      'updateInnovation',
      true
    );
  }

  async deleteInnovation(id) {
    const endpoint = `${API_CONFIG.MODULES.INNOVATION.PROJECTS}/${id}`;
    return this._handleRequest(
      'DELETE',
      endpoint,
      {},
      () => null,
      'deleteInnovation',
      true
    );
  }

  // ---------- SUPPORTING INNOVATION ENDPOINTS ----------
  async getInnovationMetrics(params = {}) {
    const endpoint = '/api/data/innovation_funnel_metrics';
    return this._handleRequest(
      'GET',
      endpoint,
      { params },
      MOCK_INNOVATION_METRICS,
      'getInnovationMetrics'
    );
  }

  async getTeams(params = {}) {
    const endpoint = '/api/data/teams';
    return this._handleRequest(
      'GET',
      endpoint,
      { params },
      MOCK_TEAMS,
      'getTeams'
    );
  }

  async getUsers(params = {}) {
    const endpoint = '/api/data/users';
    return this._handleRequest(
      'GET',
      endpoint,
      { params },
      MOCK_USERS,   // ✅ ALWAYS returns an array
      'getUsers'
    );
  }

  async getRoadmaps(params = {}) {
    const endpoint = '/api/data/roadmaps';
    return this._handleRequest(
      'GET',
      endpoint,
      { params },
      MOCK_ROADMAPS,
      'getRoadmaps'
    );
  }

  async getBusinessPlans(params = {}) {
    const endpoint = '/api/data/business_plans';
    return this._handleRequest(
      'GET',
      endpoint,
      { params },
      MOCK_BUSINESS_PLANS,
      'getBusinessPlans'
    );
  }

  // ---------- EXISTING BUSINESS ENDPOINTS ----------
  async getMetrics() {
    return this._handleRequest('GET', 'METRICS', {}, {}, 'getMetrics');
  }
  async getMilestones() {
    return this._handleRequest('GET', 'MILESTONES', {}, [], 'getMilestones');
  }
  async getMarkets() {
    return this._handleRequest('GET', 'MARKETS', {}, { markets: [], total: 0 }, 'getMarkets');
  }
  async getCompetitors() {
    return this._handleRequest('GET', 'COMPETITORS', {}, { competitors: [], total: 0 }, 'getCompetitors');
  }
  async getTeamMetrics() {
    return this._handleRequest('GET', 'TEAM_METRICS', {}, { teamSize: 15, productivity: 85, satisfaction: 4.2 }, 'getTeamMetrics');
  }
  async getFinancials() {
    return this._handleRequest('GET', 'FINANCIALS', {}, { revenue: 125000, expenses: 85000, profit: 40000 }, 'getFinancials');
  }
  async getStrategy() {
    return this._handleRequest('GET', 'STRATEGY', {}, { vision: '', goals: [] }, 'getStrategy');
  }
  async getInitiatives() {
    return this._handleRequest('GET', 'INITIATIVES', {}, [], 'getInitiatives');
  }
  async getCategories() {
    return this._handleRequest('GET', 'CATEGORIES', {}, [], 'getCategories');
  }
  async getCompanyProfile() {
    return this._handleRequest('GET', 'COMPANY_PROFILE', {}, { name: '' }, 'getCompanyProfile');
  }
  async getInvoices() {
    return this._handleRequest('GET', 'INVOICES', {}, { invoices: [], total: 0 }, 'getInvoices');
  }

  // ---------- HEALTH CHECK ----------
  async healthCheck() {
    try {
      const result = await this.client.get(API_CONFIG.UTILS.HEALTH);
      return { success: result.ok, status: result.status, data: result.data };
    } catch (error) {
      return { success: false, status: 0, error: error.message, is404: true };
    }
  }
}

// ✅ SINGLETON EXPORT
const apiServiceInstance = new ApiService();
export default apiServiceInstance;